import Dialog from "@material-ui/core/Dialog";
import { useEffect, useMemo, useState } from "react";
import {
  LuCalendar,
  LuChevronLeft,
  LuChevronRight,
  LuClock,
  LuLoader,
  LuX,
} from "react-icons/lu";
import { apiGet, apiPut, toastError, toastSuccess } from "../../../utils";

const VISIBLE_DAYS = 5;

export default function RescheduleModal({
  open,
  handleClose,
  booking,
  userId,
  onSuccess,
}) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [duration, setDuration] = useState(30);
  const [startDateOffset, setStartDateOffset] = useState(0);

  useEffect(() => {
    if (open && userId && booking) {
      const d = new Date(booking.start);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      setSelectedDate(dateStr);
      setSelectedSlot({ start: booking.start, end: booking.end });

      const durationMins = Math.round((new Date(booking.end) - new Date(booking.start)) / 60000);
      setDuration(durationMins >= 60 ? 60 : 30);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDate = new Date(d);
      bookingDate.setHours(0, 0, 0, 0);
      const diffTime = bookingDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays >= 0) {
        const newOffset = Math.floor(diffDays / VISIBLE_DAYS) * VISIBLE_DAYS;
        setStartDateOffset(newOffset);
      }
    } else if (!open) {
      setSlots([]);
      setSelectedDate(null);
      setSelectedSlot(null);
      setStartDateOffset(0);
      setDuration(30);
    }
  }, [open, userId, booking]);

  useEffect(() => {
    if (open && userId) {
      fetchSlots();
    }
  }, [open, userId, duration]);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split("T")[0];
      const twoWeeks = new Date(Date.now() + 14 * 86400000).toISOString().split("T")[0];

      const res = await apiGet(`/api/public/knowledge-partner/${userId}/slots?from=${today}&to=${twoWeeks}&duration=${duration}`);
      if (res?.data) {
        setSlots(res.data);
      }
    } catch (err) {
      toastError(err?.message || "Failed to fetch available slots");
    } finally {
      setLoading(false);
    }
  };

  const slotsByDate = useMemo(() => {
    const grouped = {};
    const allSlots = [...slots];

    if (booking && open) {
      const exists = allSlots.some(s => s.start === booking.start);
      if (!exists && new Date(booking.start) >= new Date()) {
        allSlots.push({ start: booking.start, end: booking.end });
        allSlots.sort((a, b) => new Date(a.start) - new Date(b.start));
      }
    }

    for (const slot of allSlots) {
      const d = new Date(slot.start);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!grouped[dateStr]) grouped[dateStr] = [];
      grouped[dateStr].push(slot);
    }
    return grouped;
  }, [slots, booking, open]);

  const allDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      dates.push({
        dateStr,
        dayName: d.toLocaleDateString("en-US", { weekday: "short" }),
        dayNum: d.getDate(),
        hasSlots: !!slotsByDate[dateStr]?.length,
      });
    }
    return dates;
  }, [slotsByDate]);

  const visibleDates = allDates.slice(startDateOffset, startDateOffset + VISIBLE_DAYS);

  const formatTime = (iso) => {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleReschedule = async () => {
    if (!selectedSlot || !booking) return;

    try {
      setRescheduling(true);
      await apiPut(`/api/knowledge-partner/demo-bookings/${booking.eventId}/reschedule`, {
        slotStart: selectedSlot.start,
        slotEnd: selectedSlot.end,
      });
      toastSuccess("Demo booking rescheduled successfully. Attendees have been notified.");
      onSuccess();
      handleClose();
    } catch (err) {
      toastError(err?.message || "Failed to reschedule booking");
    } finally {
      setRescheduling(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ className: "!rounded-none shadow-xl border border-slate-200 overflow-hidden m-2" }}>
      {/* Header */}
      <div className="px-3.5 py-1.5 bg-[#f8fafc] border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-[12px] font-medium text-slate-900 uppercase tracking-wider">
          Reschedule Demo
        </h3>
        <button
          onClick={handleClose}
          className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-200/80 rounded-none transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
          title="Close"
        >
          <LuX className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 bg-slate-50/40">
        {booking && (
          <div className="mb-3 p-2.5 rounded-none bg-slate-100 border border-slate-200 flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-none bg-[#313C4C] text-white flex items-center justify-center text-[11px] font-medium">
              {booking.requesterName?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-[11px] font-medium text-slate-900">
                Rescheduling with {booking.requesterName}
              </p>
              <p className="text-[10px] text-slate-500 font-medium">
                Original time: {new Date(booking.start).toLocaleString()}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-2.5">
          <h4 className="font-medium text-[11px] text-[#313C4C] uppercase tracking-wider">Select new time slot</h4>

          {/* Duration Toggle */}
          <div className="flex items-center p-0.5 rounded-none bg-slate-200/70 border border-slate-300">
            <button
              onClick={() => { setDuration(30); setSelectedSlot(null); }}
              className={`px-2.5 py-0.5 text-[10px] font-medium rounded-none transition-all uppercase tracking-wider ${duration === 30 ? "bg-[#313C4C] text-white shadow-2xs" : "text-slate-700 hover:text-slate-900"
                }`}
            >
              30 min
            </button>
            <button
              onClick={() => { setDuration(60); setSelectedSlot(null); }}
              className={`px-2.5 py-0.5 text-[10px] font-medium rounded-none transition-all uppercase tracking-wider ${duration === 60 ? "bg-[#313C4C] text-white shadow-2xs" : "text-slate-700 hover:text-slate-900"
                }`}
            >
              1 hr
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <LuLoader className="w-6 h-6 text-[#313C4C] animate-spin" />
          </div>
        ) : (
          <>
            {/* Date Row */}
            <div className="flex items-center gap-1.5 mb-3">
              <button
                onClick={() => setStartDateOffset(Math.max(0, startDateOffset - VISIBLE_DAYS))}
                disabled={startDateOffset === 0}
                className="p-1 rounded-none border border-slate-200 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:hover:bg-white"
              >
                <LuChevronLeft className="w-4 h-4 text-slate-600" />
              </button>

              <div className="flex-1 grid grid-cols-5 gap-1.5">
                {visibleDates.map((d) => (
                  <button
                    key={d.dateStr}
                    onClick={() => {
                      setSelectedDate(d.dateStr);
                      setSelectedSlot(null);
                    }}
                    disabled={!d.hasSlots}
                    className={`py-1.5 rounded-none text-center transition-all border ${selectedDate === d.dateStr
                      ? "bg-[#313C4C] text-white border-[#313C4C] shadow-2xs"
                      : d.hasSlots
                        ? "bg-white border-slate-300 hover:border-[#313C4C] text-slate-800"
                        : "bg-slate-100 border-transparent text-slate-400 cursor-not-allowed opacity-50"
                      }`}
                  >
                    <div className="text-[9px] font-medium uppercase mb-0.5 tracking-wider">
                      {d.dayName}
                    </div>
                    <div className="text-[11px] font-medium">{d.dayNum}</div>
                  </button>
                ))}
              </div>

              <button
                onClick={() =>
                  setStartDateOffset(Math.min(14 - VISIBLE_DAYS, startDateOffset + VISIBLE_DAYS))
                }
                disabled={startDateOffset + VISIBLE_DAYS >= 14}
                className="p-1 rounded-none border border-slate-200 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:hover:bg-white"
              >
                <LuChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Time Slots */}
            <div className="bg-white rounded-none border border-slate-200 p-2.5 min-h-[160px] shadow-2xs">
              {selectedDate && slotsByDate[selectedDate] ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[220px] overflow-y-auto pr-1 scroll-thin">
                  {slotsByDate[selectedDate].map((slot) => (
                    <button
                      key={slot.start}
                      onClick={() => setSelectedSlot(slot)}
                      className={`px-2 py-1.5 rounded-none text-[11px] font-medium transition-all border ${selectedSlot?.start === slot.start
                        ? "bg-[#313C4C] text-white border-[#313C4C] shadow-2xs"
                        : "bg-slate-50 border-slate-200 hover:border-slate-300 text-slate-800"
                        }`}
                    >
                      {formatTime(slot.start)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[140px] text-center">
                  <LuCalendar className="w-8 h-8 text-slate-300 mb-2" />
                  <p className="text-slate-500 font-medium text-[11px]">Select a date to view available times</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-3.5 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-[11px] font-medium text-slate-600">
          {selectedSlot ? (
            <span className="flex items-center gap-1.5 text-[#313C4C]">
              <LuClock className="w-3.5 h-3.5" />
              Selected: {new Date(selectedSlot.start).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          ) : (
            "Please select a slot"
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="h-7 px-3 text-[11px] font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 rounded-none uppercase tracking-wider transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReschedule}
            disabled={!selectedSlot || rescheduling}
            className="h-7 px-3.5 bg-[#313C4C] text-white rounded-none text-[11px] font-medium uppercase tracking-wider hover:bg-[#1e293b] transition-all shadow-xs flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rescheduling ? <LuLoader className="w-3 h-3 animate-spin" /> : null}
            Confirm Reschedule
          </button>
        </div>
      </div>
    </Dialog>
  );
}
